import React, { useCallback, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues$key } from '@atlassian/jira-relay/src/__generated__/exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import commonMessages from '../../../../common/messages.tsx';
import type { AsyncExportItem } from '../../../../common/types.tsx';
import { ProgressFlag } from '../../../../common/ui/progress-flag/index.tsx';
import messages from './messages.tsx';

type Props = {
	exportIssuesTask: exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues$key | null;
	exportItem: AsyncExportItem;
	onDismissed: () => void;
};

/**
 * Only update progress if it is higher than the current progress, this prevents updating progress with a delayed backend event
 */
const useProgress = (progress: number) => {
	const currentProgress = useRef(0);

	if (progress > currentProgress.current) {
		currentProgress.current = progress;
	}

	return currentProgress.current;
};

export const ExportIssuesTaskProgressFlag = ({
	exportIssuesTask,
	exportItem,
	onDismissed,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const exportIssuesTaskData =
		useFragment<exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues$key>(
			graphql`
				fragment exportIssuesTaskProgressFlag_issueNavigatorActionExportIssues on JiraIssueExportEvent {
					... on JiraIssueExportTaskProgress {
						task {
							id
						}
						progress
					}
					... on JiraIssueExportTaskSubmitted {
						task {
							id
						}
					}
				}
			`,
			exportIssuesTask,
		);

	const handleOnDismissed = useCallback(() => {
		onDismissed();

		const cancelEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(cancelEvent, 'cancelJiraIssueExportTask', {
			taskId: exportIssuesTaskData?.task?.id,
		});
	}, [createAnalyticsEvent, exportIssuesTaskData?.task?.id, onDismissed]);

	const id = exportIssuesTaskData?.task?.id || 'export-issues-task-progress-flag';

	const progress = fg('jira_async_export_cancel_backend_job')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useProgress(exportIssuesTaskData?.progress || 0)
		: exportIssuesTaskData?.progress || 0;

	return (
		<ProgressFlag
			onDismissed={handleOnDismissed}
			title={formatMessage(messages.exportIssuesFlagProgressTitle, {
				exportItemLabel: exportItem.label,
			})}
			description={formatMessage(messages.exportIssuesFlagProgressDescription)}
			progress={progress}
			progressAriaLabel={formatMessage(messages.exportIssuesFlagProgressAriaLabel, {
				progress: progress * 100,
			})}
			actions={[
				{
					content: formatMessage(commonMessages.exportIssuesAlreadyInProgressCancelExportAction),
					onClick: handleOnDismissed,
				},
			]}
			id={id}
		/>
	);
};
